import { Box, Flex, Text } from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  Link,
  TruncatedTextWithTooltip,
} from "@components/ui-kit";
import { LockableColumn } from "@components/ui-kit/DataTable/columns/lockable-column";
import { t } from "@lingui/macro";
import {
  ModelLockLockableTypeEnum,
  OutgoingInvoiceOrderByColumnEnum,
  useBulkDownloadOutgoingInvoicesLazyQuery,
  useMarkOutgoingInvoiceAsPaidBulkMutation,
  useSendOutgoingInvoiceBulkMutation,
} from "@src/__generated__/graphql";
import { InvoiceNoColumn } from "@src/components/modules/invoices/outgoing/components/columns";
import {
  PaidAtCell,
  PaidCell,
} from "@src/components/modules/invoices/shared/columns";
import { withProjectHeaderTooltip } from "@src/components/modules/reports/project/common";
import { ModalConfirm } from "@src/components/ui-kit";
import type { BulkAction } from "@src/components/widgets/bulk-actions/types";
import { PreviewAndDownloadCell } from "@src/components/widgets/TableCell";
import { appStore } from "@src/stores/AppStore";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { now, toApiDate } from "@src/utils/dates";
import { currency, dateTime, formatDate } from "@src/utils/formatters";
import { DatePickerModal } from "@src/utils/forms/DatePickerModal";
import type { DateRange as Range } from "@src/utils/types";
import cx from "classnames";
import { differenceInCalendarDays } from "date-fns";
import { last, map } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { useContext, useMemo, useState } from "react";
import { InvoiceTypeEnum } from "../../../Create/store";
import { EditDateWrapper } from "../../../ProformaList/components/Table/Column/edit-date-wrapper";
import type { OutgoingInvoiceModel } from "../models";
import { PageOutgoingInvoicesCtx } from "../PageOutgoingInvoices";
import { PopoverRowActions } from "./PopoverRowActions";

type TData = OutgoingInvoiceModel &
  Record<string, unknown> & { actions?: unknown };

export const TableOutgoingInvoices = observer(function TableOutgoingInvoices() {
  const store = useContext(PageOutgoingInvoicesCtx);
  const [isMarkAsPaidOpen, setIsMarkAsPaidOpen] = useState(false);
  const [isResendOpen, setIsResendOpen] = useState(false);
  const modalState = useLocalObservable(() => ({
    selectedIds: [] as string[],
    selectedDate: {
      start: now(),
      end: now(),
    },
    setSelectedIds(ids: string[]) {
      this.selectedIds = ids;
    },
    setSelectedDate(date: Range) {
      this.selectedDate = date;
    },
  }));
  const data =
    can("outgoingInvoice_read_all") ||
    can({ do: "outgoingInvoice_read_own" }) ||
    can({ do: "outgoingInvoice_read_team" })
      ? store.outgoingInvoices
      : [];
  const tableData = useMemo<TData[]>(
    () => data.map((invoice) => ({ ...invoice })),
    [data],
  );

  const [
    markOutgoingInvoiceAsPaidBulk,
    markOutgoingInvoiceAsPaidBulkMutationState,
  ] = useMarkOutgoingInvoiceAsPaidBulkMutation({
    ...onError(),
    onCompleted(res) {
      setIsMarkAsPaidOpen(false);

      if (res.markOutgoingInvoiceAsPaidBulk) {
        store.clearSelection?.();
        store.refetch();
        return;
      }

      appStore.UIStore.toast({
        title: t`Operation failed`,
        status: "error",
      });
    },
  });

  const [sendOutgoingInvoiceBulk, sendOutgoingInvoiceBulkState] =
    useSendOutgoingInvoiceBulkMutation({
      ...onError(),
      onCompleted(res) {
        setIsResendOpen(false);

        if (res.sendOutgoingInvoiceBulk) {
          store.clearSelection?.();
          store.refetch();
          return;
        }

        appStore.UIStore.toast({
          title: t`Operation failed`,
          status: "error",
        });
      },
    });

  const [fetchBulkDownloadOutgoingInvoices, bulkDownloadState] =
    useBulkDownloadOutgoingInvoicesLazyQuery({
      ...onError(),
      onCompleted(queryData) {
        if (queryData.bulkDownloadOutgoingInvoices) {
          window.location.href = queryData.bulkDownloadOutgoingInvoices;
        }
      },
    });

  const columns = useMemo<
    BodyColumnDef<TData, OutgoingInvoiceOrderByColumnEnum>[]
  >(() => {
    const cols: BodyColumnDef<TData, OutgoingInvoiceOrderByColumnEnum>[] = [
      {
        id: "invoice_no",
        meta: { label: t`Invoice no` },
        header: () => t`Invoice no`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.InvoiceNo,
        cell: ({ row }) => <InvoiceNoColumn invoice={row.original} />,
      },
      {
        id: "brands",
        meta: { label: t`Brands` },
        header: () => t`Brands`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.BrandName,
        cell: ({ row }) => (
          <Flex align="center">
            <Box>
              {map(row.original.brands, (brand) => (
                <React.Fragment key={brand.id}>
                  <Link
                    href={{
                      pathname: "/brands/detail",
                      query: { id: brand.id },
                    }}
                  >
                    {brand.name}
                  </Link>
                  {last(row.original.brands)?.id === brand.id ? null : ", "}
                </React.Fragment>
              ))}
            </Box>
          </Flex>
        ),
      },
      {
        id: "legal-entity",
        meta: { label: t`Legal entity` },
        header: () => t`Legal entity`,
        cell: ({ row }) => <Text>{row.original.workspaceCompany?.name}</Text>,
      },
      {
        id: "projects",
        meta: { label: t`Project/s` },
        header: () => t`Project/s`,
        cell: ({ row }) => (
          <Box>
            {map(row.original.projects, (project) => (
              <React.Fragment key={project.id}>
                <span
                  title={`${project.code} – ${project.title.replace(" ", "-")}`}
                >
                  <Link
                    href={{
                      pathname: "/projects/detail",
                      query: { id: project.id },
                    }}
                  >
                    <div>{project.code}</div>
                    <div>{project.title}</div>
                  </Link>
                </span>
                <br />
              </React.Fragment>
            ))}
          </Box>
        ),
      },
      {
        id: "amount",
        meta: { label: t`Amount` },
        header: () => t`Amount`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.Amount,
        cell: ({ row }) =>
          currency.formatByCurrency(
            row.original.stats.total_amount_in_currency,
            row.original.currency,
          ),
      },
      {
        id: "amount_with_vat",
        meta: { label: t`Amount (VAT)` },
        header: () => t`Amount (VAT)`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.AmountWithVat,
        cell: ({ row }) =>
          currency.formatByCurrency(
            row.original.stats.total_amount_with_vat_in_currency,
            row.original.currency,
          ),
      },
      {
        id: "billing_amount",
        meta: {
          label: t`Billing amount`,
        },
        header: withProjectHeaderTooltip(
          t`Billing amount`,
          t`Amount credited to billing, at brand or project level.`,
        ),
        cell: ({ row }) =>
          currency.formatByWorkspace(row.original.stats.billing_amount),
      },
      {
        id: "billing_amount_with_vat",
        meta: {
          label: t`Billing amount (VAT)`,
        },
        header: withProjectHeaderTooltip(
          t`Billing amount (VAT)`,
          t`Amount credited to billing, at brand or project level.`,
        ),
        cell: ({ row }) =>
          currency.formatByWorkspace(
            row.original.stats.billing_amount_with_vat,
          ),
      },
      {
        id: "date_of_supply",
        meta: { label: t`Supply date` },
        header: () => t`Supply date`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.DateOfSupply,
        cell: ({ row }) => (
          <EditDateWrapper
            row={row.original}
            invoiceType={InvoiceTypeEnum.Outgoing}
            dataComponentName="issue-date-edit-trigger"
            isDisabled={!row.original.date_of_supply}
            onUpdate={store.refetch}
            modalStore={store.changeDatesModalStore}
          >
            {dateTime.format(row.original.date_of_supply)}
          </EditDateWrapper>
        ),
      },
      {
        id: "issue_date",
        meta: { label: t`Issue date` },
        header: () => t`Issue date`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.IssueDate,
        cell: ({ row }) => (
          <EditDateWrapper
            row={row.original}
            invoiceType={InvoiceTypeEnum.Outgoing}
            dataComponentName="issue-date-edit-trigger"
            isDisabled={!row.original.issue_date}
            onUpdate={store.refetch}
            modalStore={store.changeDatesModalStore}
          >
            {dateTime.format(row.original.issue_date)}
          </EditDateWrapper>
        ),
      },
      {
        id: "due_date",
        meta: { label: t`Due date` },
        header: () => t`Due date`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.DueDate,
        cell: ({ row }) => (
          <EditDateWrapper
            dataComponentName="due-date-edit-trigger"
            row={row.original}
            invoiceType={InvoiceTypeEnum.Outgoing}
            isDisabled={!row.original.due_date}
            onUpdate={store.refetch}
            modalStore={store.changeDatesModalStore}
          >
            <div
              className={cx({
                "text-red":
                  differenceInCalendarDays(new Date(), row.original.due_date) >
                    0 && !row.original.paid_at,
              })}
            >
              {dateTime.format(row.original.due_date)}
              {differenceInCalendarDays(new Date(), row.original.due_date) >
                0 &&
                !row.original.paid_at && (
                  <span className="block">
                    (
                    {differenceInCalendarDays(
                      new Date(),
                      row.original.due_date,
                    )}{" "}
                    {t`days before`})
                  </span>
                )}
            </div>
          </EditDateWrapper>
        ),
      },
      {
        id: "submitted_at",
        meta: { label: t`Submitted at` },
        header: () => t`Submitted at`,
        cell: ({ row }) =>
          row.original.submitted_at
            ? formatDate(new Date(row.original.submitted_at))
            : null,
      },
      {
        id: "created_at",
        meta: { label: t`Created at` },
        header: () => t`Created at`,
        cell: ({ row }) =>
          row.original.created_at
            ? formatDate(new Date(row.original.created_at))
            : null,
      },
      {
        id: "paid",
        meta: { label: t`Paid` },
        header: () => t`Paid`,
        cell: ({ row }) => (
          <PaidCell paid={row.original.paid} currency={row.original.currency} />
        ),
      },
      {
        id: "paid_at",
        meta: { label: t`Paid at` },
        header: () => t`Paid at`,
        orderBy: OutgoingInvoiceOrderByColumnEnum.PaymentStatus,
        cell: ({ row }) => (
          <EditDateWrapper
            invoiceType={InvoiceTypeEnum.Outgoing}
            dataComponentName="paid-date-edit-trigger"
            row={row.original}
            isDisabled={!row.original.paid_at}
            onUpdate={store.refetch}
            modalStore={store.changeDatesModalStore}
          >
            <PaidAtCell
              paidAt={row.original.paid_at}
              hasPayment={!!row.original.paid && row.original.paid !== 0}
            />
          </EditDateWrapper>
        ),
      },
      {
        id: "modelLock",
        header: () => "",
        meta: { label: t`Lock` },
        size: 40,
        cell: ({ row }) => (
          <LockableColumn
            modelLock={row.original.modelLock}
            canToggle={can("outgoingInvoice_modelLock")}
            onClick={(locked) =>
              locked
                ? store.unlockOutgoingInvoice(row.original.id)
                : store.lockOutgoingInvoice(row.original.id)
            }
            isLoading={appStore.modelLock.isLoading(
              row.original.id,
              ModelLockLockableTypeEnum.OutgoingInvoice,
            )}
          />
        ),
      },
      {
        id: "invoicePdf",
        meta: { label: t`Invoice` },
        header: () => t`Invoice`,
        cell: ({ row }) => (
          <PreviewAndDownloadCell
            value={[
              {
                label: t`Proforma invoice`,
                value: row.original.relatesToProformaPdf,
              },
              {
                label: t`Payment receipt`,
                value: row.original.invoicePdf,
              },
            ]}
          />
        ),
      },
      {
        id: "payment_method",
        meta: { label: t`Payment method` },
        header: () => t`Payment method`,
        cell: ({ row }) => row.original.invoicePaymentMethod?.name ?? "—",
      },
      {
        id: "note",
        meta: { label: t`Note` },
        header: () => t`Note`,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.note} />
        ),
      },
      {
        id: "internal_note",
        meta: { label: t`Internal note` },
        header: () => t`Internal note`,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.internal_note} />
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) => (
          <PopoverRowActions model={row.original} id={row.original.id} />
        ),
      },
    ];

    return appStore.workspaceStore.hasMultipleCompanies()
      ? cols
      : cols.filter((column) => column.id !== "legal-entity");
  }, [store.changeDatesModalStore, store.refetch]);

  const bulkActions = useMemo<BulkAction[]>(() => {
    const bulkActions: BulkAction[] = [
      {
        id: "outgoing-invoices-actions",
        label: () => t`Actions`,
        icon: "dots-vertical",
        isLoading: () =>
          markOutgoingInvoiceAsPaidBulkMutationState.loading ||
          sendOutgoingInvoiceBulkState.loading ||
          bulkDownloadState.loading ||
          store.isBulkLockToggling,
        subActions: [
          ...(can("outgoingInvoice_markAsPaid")
            ? [
                {
                  id: "mark-as-paid",
                  label: () => t`Mark as paid`,
                  callback: (selectedIds: string[] | Set<string>) => {
                    modalState.setSelectedIds(Array.from(selectedIds));
                    modalState.setSelectedDate({
                      start: now(),
                      end: now(),
                    });
                    setIsMarkAsPaidOpen(true);
                  },
                },
              ]
            : []),
          ...(can("outgoingInvoice_sendToClient")
            ? [
                {
                  id: "resend-invoice",
                  label: () => t`Resend invoice`,
                  callback: (selectedIds: string[] | Set<string>) => {
                    modalState.setSelectedIds(Array.from(selectedIds));
                    setIsResendOpen(true);
                  },
                },
              ]
            : []),
          {
            id: "download-pdf",
            label: () => t`Download PDF`,
            callback: (selectedIds: string[] | Set<string>) => {
              fetchBulkDownloadOutgoingInvoices({
                variables: { ids: Array.from(selectedIds) },
              });
            },
          },
        ],
      },
    ];

    if (can("outgoingInvoice_modelLock")) {
      bulkActions.push(
        {
          id: "lock",
          label: () => t`Lock`,
          icon: "lock-01",
          isLoading: () => store.isBulkLocking,
          callback: (selectedIds: string[] | Set<string>) => {
            store.bulkLock(Array.from(selectedIds));
          },
        },
        {
          id: "unlock",
          label: () => t`Unlock`,
          icon: "lock-unlocked-01",
          iconColorScheme: "red",
          isLoading: () => store.isBulkUnlocking,
          callback: (selectedIds: string[] | Set<string>) => {
            store.bulkUnlock(Array.from(selectedIds));
          },
        },
      );
    }

    return bulkActions;
  }, [
    bulkDownloadState.loading,
    fetchBulkDownloadOutgoingInvoices,
    markOutgoingInvoiceAsPaidBulkMutationState.loading,
    modalState,
    sendOutgoingInvoiceBulkState.loading,
  ]);

  return (
    <React.Fragment>
      <DataTable.Body<TData, OutgoingInvoiceOrderByColumnEnum>
        tableKey="outgoing-invoices"
        data={tableData}
        columns={columns}
        enableRowSelection
        bulkActions={bulkActions}
        orderedBy={store.orderBy.clauses[0]}
        onOrderByChange={(clause) => {
          if (!clause.order) {
            store.orderBy.setClauses([]);
            store.pagination.resetPage();
            return;
          }

          store.orderBy.setClauses([
            { column: clause.column, order: clause.order },
          ]);
          store.pagination.resetPage();
        }}
        selected={store.selectedOutgoingInvoicesIds}
        onSelect={(ids) => (store.selectedOutgoingInvoicesIds = ids)}
        allIds={store.outgoingInvoicesSummary?.outgoingInvoiceIds ?? []}
      />

      <ModalConfirm
        isOpen={isResendOpen}
        onClose={() => setIsResendOpen(false)}
        onConfirm={() =>
          sendOutgoingInvoiceBulk({
            variables: { ids: modalState.selectedIds },
          })
        }
        isLoading={sendOutgoingInvoiceBulkState.loading}
      />

      <DatePickerModal
        isOpen={isMarkAsPaidOpen}
        onClose={() => setIsMarkAsPaidOpen(false)}
        setSelectedDate={modalState.setSelectedDate}
        selectedDate={modalState.selectedDate}
        title={t`Select paid date`}
        primaryAction={{
          title: t`Mark as paid`,
          action: () =>
            markOutgoingInvoiceAsPaidBulk({
              variables: {
                id: modalState.selectedIds,
                date: toApiDate(modalState.selectedDate.start),
              },
            }),
        }}
      />
    </React.Fragment>
  );
});
